import Axios, {
  AxiosError,
  AxiosInstance, AxiosResponse,
} from 'axios';
import { ILocalStorageService } from '../local-storage-service';
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY, ACCESS_TOKEN_EXPIRY_KEY } from '../../modules/user/user.constants';
import { RefreshTokenDTO } from '../../modules/user/dto/refresh-token.dto';
import { ISignInResponse } from '../../modules/user/dto/sign-in.dto';
import {
  IRequestMethodParams,
  IRequestParamsWithMethod,
  IApiService,
  IRefreshRequest,
  IRefreshResponse,
} from './i-api.service';

export class ApiService implements IApiService {
  protected readonly axios: AxiosInstance;

  protected readonly handleError: (error: AxiosError) => void;

  protected readonly handleSuccess: (res: AxiosResponse) => void;

  private readonly localStorageService: ILocalStorageService;

  private putTimer : null | unknown;

  private postTimer : null | unknown;

  private deleteTimer : null | unknown;

  constructor(baseURL: string,
    handleError: (error: AxiosError) => void,
    handleSuccess: (error: AxiosResponse) => void,
    localStorageService: ILocalStorageService) {
    this.axios = Axios.create({ baseURL });
    this.handleError = handleError;
    this.handleSuccess = handleSuccess;
    this.localStorageService = localStorageService;
    this.createAxiosResponseInterceptor();
  }

  async get<T>(
    requestParams: IRequestMethodParams<undefined>,
  ): Promise<T> {
    return this.request<undefined, T>({ method: 'get', ...requestParams });
  }

  async post<P, T>(
    requestParams: IRequestMethodParams<P>,
  ): Promise<T> {
    return this.request<P, T>({ method: 'post', ...requestParams });
  }

  async put<P, T>(
    requestParams: IRequestMethodParams<P>,
  ): Promise<T> {
    return this.request<P, T>({ method: 'put', ...requestParams });
  }

  async patch<P, T>(
    requestParams: IRequestMethodParams<P>,
  ): Promise<T> {
    return this.request<P, T>({ method: 'patch', ...requestParams });
  }

  async delete<T>(
    requestParams: IRequestMethodParams<undefined>,
  ): Promise<T> {
    return this.request<undefined, T>({ method: 'delete', ...requestParams });
  }

  private createAxiosResponseInterceptor() {
    const interceptor = this.axios.interceptors.response.use(
      (response) => response,
      (error) => {
        /*
          * Guard the response. A cancelled or timed-out request rejects with no
          * `response` at all, and reading `.status` off it used to throw a
          * TypeError inside this handler — so the caller saw a TypeError,
          * Axios.isCancel() was false, and a user-initiated cancel was reported
          * as a failed request.
        */
        if (!error.response || error.response.status !== 401) {
          return Promise.reject(error);
        }
        /*
          * When response code is 401, try to refresh the token.
          * Eject the interceptor so it doesn't loop in case
          * token refresh causes the 401 response
        */
        this.axios.interceptors.response.eject(interceptor);

        return this.post<IRefreshRequest, IRefreshResponse>(
          {
            url: '/user-manager/api/refresh',
            data: { refreshToken: this.localStorageService.get(REFRESH_TOKEN_KEY) },
            silent: true,
          },
        ).then((data) => {
          this.localStorageService.set(ACCESS_TOKEN_KEY, data.accessToken);
          this.localStorageService.set(ACCESS_TOKEN_EXPIRY_KEY, data.accessTokenExpiry);
          this.localStorageService.set(REFRESH_TOKEN_KEY, data.refreshToken);
          // eslint-disable-next-line no-param-reassign
          error.response.config.headers.Authorization = `Bearer ${data.accessToken}`;
          return this.axios(error.response.config);
        });
      },
    );
  }

  private handleNotificationsQueue(method : string, response :AxiosResponse) {
    if (method === 'put') {
      clearTimeout(this.putTimer as number);
      this.putTimer = setTimeout(() => {
        this.handleSuccess(response);
      }, 400) as unknown;
    }

    if (method === 'post') {
      clearTimeout(this.postTimer as number);
      this.postTimer = setTimeout(() => {
        this.handleSuccess(response);
      }, 400) as unknown;
    }

    if (method === 'delete') {
      clearTimeout(this.deleteTimer as number);
      this.deleteTimer = setTimeout(() => {
        this.handleSuccess(response);
      }, 400) as unknown;
    }
  }

  async getRefresh() : Promise<void> {
    try {
      const payload = await this.post<RefreshTokenDTO, ISignInResponse>({
        url: 'user-manager/api/refresh',
        silent: true,
        data: {
          refreshToken: this.localStorageService.get(REFRESH_TOKEN_KEY) || '',
        },
      });
      this.localStorageService.set(ACCESS_TOKEN_KEY, payload.accessToken);
      this.localStorageService.set(ACCESS_TOKEN_EXPIRY_KEY, payload.accessTokenExpiry);
      this.localStorageService.set(REFRESH_TOKEN_KEY, payload.refreshToken);
    } catch (err) {
      throw new Error(err);
    }
  }

  async request<P, T>(
    requestParams: IRequestParamsWithMethod<P>,
  ): Promise<T> {
    const {
      data,
      headers,
      method,
      params,
      url,
      responseType,
      silent,
      onUploadProgress,
      cancelToken,
      timeout,
    } = requestParams;
    try {
      let accessToken = this.localStorageService.get(ACCESS_TOKEN_KEY);
      if (accessToken) {
        accessToken = `Bearer ${accessToken}`;
      }
      const response: AxiosResponse = await this.axios.request({
        data,
        headers: { ...headers, Authorization: accessToken },
        method,
        params,
        url,
        responseType,
        onUploadProgress,
        cancelToken,
        timeout,
      });

      this.handleNotificationsQueue(method, response);
      return response.data as T;
    } catch (error) {
      if (!requestParams.url.includes('forgotPassword') && !silent) {
        this.handleError(error);
      }
      throw error;
    }
  }
}

export default ApiService;
